import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setAuthUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL hash
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          console.error('No session_id found in URL');
          navigate('/login', { replace: true });
          return;
        }

        const sessionId = sessionIdMatch[1];
        
        // Exchange session_id for app session
        const response = await axios.post(`${API}/auth/session`, 
          { session_id: sessionId },
          { withCredentials: true }
        );

        if (response.data.success && response.data.user) {
          setAuthUser(response.data.user);
          window.history.replaceState(null, '', window.location.pathname);

          // ── Check for pending invite (localStorage survives OAuth redirects on all browsers) ──
          let inviteToken = null;
          try {
            const raw = localStorage.getItem('mb_pending_invite');
            if (raw) {
              const { token: t, ts } = JSON.parse(raw);
              localStorage.removeItem('mb_pending_invite'); // always clear
              // Only use if stored within last 10 minutes
              if (t && Date.now() - ts < 10 * 60 * 1000) {
                inviteToken = t;
              }
            }
          } catch { /* ignore */ }

          if (inviteToken) {
            try {
              const acceptRes = await axios.post(
                `${API}/groups/accept-invite`,
                { token: inviteToken },
                { withCredentials: true }
              );
              if (acceptRes.data.success && acceptRes.data.group_id) {
                navigate(`/groups/${acceptRes.data.group_id}`, { replace: true });
                return;
              }
            } catch (err) {
              const detail = err.response?.data?.detail;
              if (detail === 'wrong_account') {
                navigate(`/groups/invite/${inviteToken}`, { replace: true });
                return;
              }
              // Token expired/used — fall through to dashboard
            }
          }

          navigate('/dashboard', { replace: true, state: { user: response.data.user } });
        } else {
          navigate('/login', { replace: true });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate, setAuthUser]);

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: "linear-gradient(180deg, #E8EEFF 0%, #F4F6FF 60%, #FFFFFF 100%)" }}
    >
      <div className="text-center">
        <div
          className="w-9 h-9 rounded-full animate-spin mx-auto mb-4"
          style={{ border: "3px solid #E0E7FF", borderTopColor: "#2D5BFF" }}
        />
        <p style={{ fontFamily: "DM Sans, sans-serif", color: "#6B7280", fontSize: 14 }}>
          Signing you in…
        </p>
      </div>
    </div>
  );
}